(() => {
  'use strict';

  const EVENT_PREFIX = 'choices_try_on_';
  const WAIT_COPY = 'Creating your personalized Choices preview. This can take 1–2 minutes per view. Please keep this window open.';
  const initialized = new WeakSet();

  function productHandle(widget) {
    return widget?.dataset.productHandle || location.pathname.split('/products/')[1]?.split(/[?#/]/)[0] || '';
  }

  function publish(name, widget, detail = {}) {
    const payload = {
      event: `${EVENT_PREFIX}${name}`,
      timestamp: new Date().toISOString(),
      path: location.pathname,
      product_handle: productHandle(widget),
      ...detail
    };
    document.dispatchEvent(new CustomEvent('choices:try-on-analytics', { detail: payload }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function parseSelectedVariant() {
    const script = document.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]');
    try { return JSON.parse(script?.textContent || 'null'); } catch (_error) { return null; }
  }

  function normalizeUrl(value) {
    if (!value) return '';
    try { return new URL(String(value), location.origin).href; } catch (_error) { return String(value); }
  }

  function currentVariantId() {
    return String(document.querySelector('.product-form input[name="id"], form[action*="/cart/add"] input[name="id"]')?.value || parseSelectedVariant()?.id || '');
  }

  function selectedImage(variant) {
    return normalizeUrl(
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_media?.preview_image?.url ||
      variant?.featured_image?.src ||
      variant?.featured_image?.url ||
      document.querySelector('media-gallery .is-active img, .product__media-item.is-active img, .product__media img')?.currentSrc ||
      document.querySelector('media-gallery .is-active img, .product__media-item.is-active img, .product__media img')?.src
    );
  }

  function clearStale(widget) {
    widget.resultsBySize = {};
    widget.results = {};
    widget.currentAngle = 'front';
    const image = widget.querySelector('[data-result-image]');
    if (image) {
      image.removeAttribute('src');
      image.hidden = true;
    }
  }

  function syncVariant(widget) {
    const variant = parseSelectedVariant();
    const variantId = String(variant?.id || currentVariantId());
    const imageUrl = selectedImage(variant);
    const previous = widget.dataset.selectedVariantId || '';

    if (previous && variantId && previous !== variantId) clearStale(widget);
    if (variantId) widget.dataset.selectedVariantId = variantId;
    if (variant?.title) widget.dataset.selectedVariantTitle = variant.title;
    if (imageUrl) {
      widget.dataset.garmentFrontImage = imageUrl;
      widget.dataset.garmentImage = imageUrl;
      widget.dataset.authoritativeVariantImage = imageUrl;
    }
  }

  function addPrivacyNote(widget) {
    if (widget.querySelector('[data-choices-privacy-note]')) return;
    const note = document.createElement('p');
    note.dataset.choicesPrivacyNote = 'true';
    note.className = 'choices-try-on__privacy';
    note.textContent = 'Your uploaded photo is used only to create this preview and is not retained after processing. Generated previews expire automatically after about two hours.';
    note.style.cssText = 'font-size:12px;line-height:1.45;opacity:.72;margin:.75rem 0;';
    const button = widget.querySelector('[data-generate]');
    if (button) button.insertAdjacentElement('beforebegin', note);
  }

  function setup(widget) {
    if (!widget || initialized.has(widget)) return;
    initialized.add(widget);
    syncVariant(widget);
    addPrivacyNote(widget);

    const loadingPanel = widget.querySelector('[data-panel="loading"]');
    const loadingMessage = widget.querySelector('[data-loading-message]');
    const resultPanel = widget.querySelector('[data-panel="result"]');
    const errorNode = widget.querySelector('[data-error]');

    widget.querySelector('[data-try-on-open]')?.addEventListener('click', () => {
      syncVariant(widget);
      publish('opened', widget, { variant_id: widget.dataset.selectedVariantId || '' });
    }, true);
    widget.querySelector('[data-generate]')?.addEventListener('click', () => {
      syncVariant(widget);
      publish('generation_started', widget, { view: 'front', variant_id: widget.dataset.selectedVariantId || '' });
    }, true);
    widget.querySelector('[data-regenerate]')?.addEventListener('click', () => {
      syncVariant(widget);
      publish('generation_started', widget, { view: 'front', regeneration: true, variant_id: widget.dataset.selectedVariantId || '' });
    }, true);

    if (loadingPanel && loadingMessage) {
      const enforceCopy = () => {
        if (!loadingPanel.hidden && loadingMessage.textContent !== WAIT_COPY) loadingMessage.textContent = WAIT_COPY;
      };
      new MutationObserver(enforceCopy).observe(loadingPanel, { attributes: true, attributeFilter: ['hidden'] });
      enforceCopy();
    }

    if (resultPanel) {
      new MutationObserver(() => {
        if (!resultPanel.hidden) publish('generation_completed', widget);
      }).observe(resultPanel, { attributes: true, attributeFilter: ['hidden'] });
    }

    if (errorNode) {
      new MutationObserver(() => {
        if (!errorNode.hidden && errorNode.textContent.trim()) {
          publish('generation_failed', widget, { message: errorNode.textContent.trim().slice(0, 180) });
        }
      }).observe(errorNode, { attributes: true, childList: true, subtree: true });
    }
  }

  function setupExisting(root = document) {
    root.querySelectorAll?.('choices-try-on').forEach(setup);
  }

  function scheduleVariantSync() {
    document.querySelectorAll('choices-try-on').forEach(syncVariant);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-try-on-open], [data-generate], [data-regenerate]');
    if (!trigger) return;
    const widget = trigger.closest('choices-try-on') || document.querySelector('choices-try-on');
    if (widget) setup(widget);
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('variant-selects input, variant-selects select, variant-radios input, variant-radios select, .product-form input[name="id"]')) {
      window.setTimeout(scheduleVariantSync, 0);
      window.setTimeout(scheduleVariantSync, 180);
    }
  }, true);

  document.addEventListener('shopify:section:load', (event) => setupExisting(event.target));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupExisting(), { once: true });
  else setupExisting();
})();
